import classNames from "classnames"
import { Children, cloneElement, isValidElement, ReactElement, useMemo } from "react"
import { validateDisplayNameChild } from "../utils/validateDisplayNameChild"
import { devWarning } from "../utils/devWarning"
import { ButtonGroupProps } from "./types"

const CLASSES = [
  "inline-flex",
  "[&>button]:rounded-none",
  "[&>button:focus-visible]:z-20",
  "[&>button:focus]:relative",
  "[&>button:first-of-type]:rounded-l-lg",
  "[&>button:last-of-type]:rounded-r-lg",
  "[&>button:not(:first-of-type)]:-ml-0.5",
]

export function ButtonGroup({
  active,
  className,
  children,
  size = "md",
  ...rest
}: ButtonGroupProps) {
  const _className = classNames(CLASSES, "isolate", className)
  const _children = useMemo(() => {
    const arrayOfChild: ReactElement[] = []
    Children.toArray(children).forEach(child => {
      if (isValidElement(child) && validateDisplayNameChild(child, "Button")) {
        const isActive = typeof active !== "undefined" && active === child.props.id
        arrayOfChild.push(
          cloneElement(child, {
            ...child.props,
            "aria-pressed": isActive,
            size,
            variation: "outline",
            className: classNames(
              child.props.className,
              "aria-pressed:bg-primary-lighten-100 aria-pressed:z-10 aria-pressed:relative aria-pressed:border-primary-base aria-pressed:text-primary-base",
              {
                "[&_.badge]:bg-white [&_.badge]:text-primary-base [&_.badge]:border-primary-base [&_.badge]:border":
                  isActive,
              },
            ),
          }),
        )
      } else {
        devWarning(
          true,
          "`ButtonGroup` only accepts `Button` as children. Ignoring child `",
          child,
          "",
        )
      }
    })

    return arrayOfChild
  }, [active, children, size])

  return (
    <div {...rest} role="group" className={_className}>
      {_children}
    </div>
  )
}

ButtonGroup.displayName = "ButtonGroup"
